import { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useUnifiedAuth } from '@/lib/auth/use-unified-auth';
import { useAdminStore } from "../../lib/stores/admin-store";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { Shield, Users, FileCheck, History, Menu, X, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface AdminLayoutProps {
  children: ReactNode;
}

export function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile } = useUnifiedAuth();
  const { isInitialized, error } = useAdminStore();
  const [menuOpen, setMenuOpen] = useState(false);

  // Fix React Router warning by using useEffect for navigation
  useEffect(() => {
    // Redirect if not admin - use session user role first
    const userRole = user?.user_metadata?.role || profile?.role;
    if (user && userRole !== 'admin') {
      navigate("/");
    }
  }, [user, profile, navigate]);

  // Close mobile menu when changing routes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <ErrorMessage message={error} />;
  }

  const navItems = [
    { path: "/admin", label: "Overview", icon: Shield },
    { path: "/admin/users", label: "Users", icon: Users },
    { path: "/admin/experiences", label: "Experiences", icon: FileCheck },
    { path: "/admin/payments", label: "Payments", icon: CreditCard },
    { path: "/admin/transfers", label: "Transfers", icon: History },
    { path: "/admin/activity", label: "Activity Log", icon: History },
  ];

  // For proper layout nesting with the main site header
  const isActive = (path: string) => {
    if (path === "/admin") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-earth-900 pt-16">
      {/* Admin Header Mobile Toggle */}
      <div className="bg-earth-800 fixed top-16 left-0 right-0 z-30 flex justify-between items-center h-12 px-4 md:hidden border-b border-earth-700">
        <h1 className="text-lg font-display text-sand-100">Rendezvous Admin</h1>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="p-2 text-sand-300 hover:bg-earth-700 rounded-md"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
        >
          {menuOpen ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-10 md:hidden top-28"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 mt-12 md:mt-0">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Admin Navigation - Mobile Drawer */}
          <nav 
            className={`
              fixed md:relative top-28 md:top-0 left-0 right-0 z-20
              bg-earth-800 md:bg-transparent h-auto md:w-64 md:flex-shrink-0
              transform ${menuOpen ? 'translate-y-0' : '-translate-y-full md:translate-y-0'} 
              transition-transform duration-200 ease-in-out md:transform-none
              shadow-lg md:shadow-none border-b border-earth-700 md:border-none
              overflow-y-auto max-h-[calc(100vh-7rem)] md:max-h-none
            `}
          >
            <div className="bg-earth-800 shadow rounded-none md:rounded-lg p-4">
              <div className="hidden md:block mb-4">
                <h2 className="text-xl font-display text-sand-100">Admin Panel</h2>
                <p className="text-sm text-sand-400">Manage your marketplace</p>
              </div>
              <div className="space-y-2">
                {navItems.map(({ path, label, icon: Icon }) => (
                  <Link 
                    key={path} 
                    to={path}
                    className={`
                      flex items-center px-4 py-3 md:py-2 text-sm w-full justify-start rounded-md
                      ${isActive(path)
                        ? "bg-sand-400 text-earth-900 hover:bg-sand-300" 
                        : "bg-earth-700 text-sand-300 hover:bg-earth-600 hover:text-sand-200"
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 md:h-4 md:w-4 mr-3 md:mr-2" />
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Main Content with proper spacing for mobile */}
          <main className="flex-1">
            <div className="bg-earth-800 shadow rounded-lg">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
} 